/** @jsx jsx */

import { isEmpty, truncate } from 'lodash';
import { useState } from 'react';

import {
  Button,
  Grid,
  Margin,
  ModalDialogRoot,
  ModalDialogBody,
  Spacer,
  Surface,
} from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import InfoCount from 'app/developer-v3/components/InfoCount';

import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import Markdown from 'app/developer-v3/components/Markdown';
import createRenderCallback from 'app/common/components/createRenderCallback';
import { AppSummary } from 'app/developer-v3/types';
import { getAppUrl } from 'app/developer-v3/utils';
import { getStatusHelpers } from 'app/developer-v3/hooks/useIssue/utils';
import {
  useActiveProfile,
  useIssue,
  usePartnerProfile,
  useRouter,
} from 'app/developer-v3/hooks';

import AuthorInfo from './AuthorInfo';
import CommentEditorPanel from './CommentEditorPanel';
import Stream from '../Stream';
import TagGroup from '../TagGroup';
import WorkStatusForm from './WorkStatusForm';
import IssueTag, { IssueStatus } from '../IssueTag';
import { WORK_STATUSES_DISPLAY } from '../constants';

const Styles = {
  header: css`
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: 15px;
  `,
  issueBody: css`
    padding: 20px;
  `,
  buttonWrapper: css`
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  `,
};

export type Props = {
  appSummary: AppSummary;
  isLoading: boolean;
  isSaving: boolean;
};

function IssueDetails({ appSummary, isLoading, isSaving }: Props) {
  const {
    activeProfile,
    isLoading: isLoadingActiveProfile,
  } = useActiveProfile();
  const isStaff = activeProfile?.isStaff ?? false;
  const {
    isLoading: isLoadingPartnerProfile,
    partnerProfile,
  } = usePartnerProfile();
  const { email } = partnerProfile;
  const { params, queryPrevious, urlPrevious } = useRouter();
  const { id: integrationId, issueId } = params;

  const {
    comment,
    isLoading: isLoadingIssues,
    isMutatingIssue,
    issue,
    toggleStatus,
  } = useIssue({
    integrationId: Number(integrationId),
    issueId: Number(issueId),
  });

  // Controls for Update Status modal
  const [isShowingModal, setIsShowingModal] = useState(false);
  const openModal = () => setIsShowingModal(true);
  const closeModal = () => setIsShowingModal(false);
  const isOpen = isShowingModal || isSaving;

  if (
    isLoadingActiveProfile ||
    isLoadingPartnerProfile ||
    isLoadingIssues ||
    !issue
  ) {
    return null;
  }

  const issueStatus = getStatusHelpers(issue.status);

  const issuesRoute = `${getAppUrl(integrationId)}/issues`;
  const breadcrumbs = [
    {
      text: 'Issues',
      // If we're coming from a filtered issues page, we keep the filters.
      url: urlPrevious?.startsWith(issuesRoute)
        ? `${issuesRoute}?${new URLSearchParams(queryPrevious).toString()}`
        : issuesRoute,
    },
    { text: truncate(issue.title, { length: 50, separator: ' ' }) },
  ];

  return (
    <ManagerLayout
      breadcrumbs={breadcrumbs}
      renderDescription={createRenderCallback(AuthorInfo, {
        createdAt: issue.createdAt,
        email: issue.author?.email,
        isStaff,
        name: issue.author?.publicAlias
          ? issue.author?.publicAlias
          : issue.author?.name,
      })}
      shouldLimitWidth={true}
      title={issue.title}
    >
      <header css={Styles.header}>
        <TagGroup>
          {issue.isBlocking && <IssueTag type={IssueStatus.Blocking} />}
          <IssueTag type={issue.typeOfDisplay} />
          <IssueTag type={issue.statusDisplay} />
          {issueStatus.isOpen && (
            <IssueTag
              type={
                // fall back to Not Started if the Work Status is null
                issue.workStatusDisplay || WORK_STATUSES_DISPLAY.notStarted
              }
            />
          )}
        </TagGroup>
        <Grid columns="repeat(2, auto)" gap={20}>
          <InfoCount
            count={issue.commentsCount}
            icon="commentBlankFill"
            infoName="Comments"
          />
          <InfoCount
            count={issue.numInterestedUsers}
            icon="personGroupFill"
            infoName="Affected Users"
          />
        </Grid>
        {issueStatus.isOpen && (
          <Button onPress={openModal} size="compact">
            Update Status
          </Button>
        )}
        <ModalDialogRoot
          isOpen={isOpen}
          onOpenChange={open => {
            if (!open) closeModal();
          }}
          aria-label="Update Status"
        >
          <ModalDialogBody>
            <WorkStatusForm
              initialValues={{
                workStatus: null,
                resolutionStatus: null,
                statusComment: '',
              }}
              isLoading={isLoading}
              isSaving={isSaving}
              onCancel={closeModal}
              onSubmit={closeModal}
            />
          </ModalDialogBody>
        </ModalDialogRoot>
      </header>
      <Margin margin="30px 0px 30px">
        <Surface borderColor="GrayWarm4">
          <section css={Styles.issueBody}>
            <Markdown source={issue.body} />
          </section>
        </Surface>
      </Margin>
      <Stream
        accountOwnerEmail={email}
        appId={Number(integrationId)}
        appImageUrl={appSummary.imageUrl}
        appTitle={appSummary.title}
        isSaving={comment.isSaving}
        issue={issue}
        onSubmit={comment.save}
      />
      <Spacer height={40} />
      <CommentEditorPanel isSaving={comment.isSaving} onSubmit={comment.save} />
      {!issueStatus.isClosed && !appSummary.isPublished && (
        <div css={Styles.buttonWrapper}>
          <Button
            aria-label={
              isEmpty(issue.comments)
                ? 'Leave a comment before resolving the issue'
                : ''
            }
            variant={issueStatus.isResolved ? 'secondary' : 'primary'}
            isDisabled={isEmpty(issue.comments) || isMutatingIssue}
            onPress={() => toggleStatus(issue.status)}
          >
            {`Mark ${issueStatus.isResolved ? 'unresolved' : 'resolved'}`}
          </Button>
        </div>
      )}
    </ManagerLayout>
  );
}

export default IssueDetails;
